import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreVertical,
  Star,
  Eye,
  Edit,
  Trash2,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { getAllPropFirms, deletePropFirm } from '@/services/propFirmApi';

export const PropFirmsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteFirm, setDeleteFirm] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // ==================== FETCH ====================
  const fetchFirms = async () => {
    try {
      setLoading(true);
      const data = await getAllPropFirms();
      // Handle both array and { data: [...] }
      const list = Array.isArray(data) ? data : data?.data || [];
      setFirms(list);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load firms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirms();
  }, []);

  // ==================== FILTER ====================
  const filteredFirms = firms.filter((firm) => {
    const matchesSearch = firm.name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      firm.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  // ==================== HANDLERS ====================
  const handleToggleFeatured = (firmId, currentValue) => {
    // Local only for now (no API for featured toggle)
    setFirms(
      firms.map((f) =>
        f.id === firmId ? { ...f, featured: !currentValue } : f
      )
    );
    toast.success(!currentValue ? 'Firm featured' : 'Firm unfeatured');
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedFirms(filteredFirms.map((f) => f.id));
    } else {
      setSelectedFirms([]);
    }
  };

  const handleSelectFirm = (firmId, checked) => {
    if (checked) {
      setSelectedFirms([...selectedFirms, firmId]);
    } else {
      setSelectedFirms(selectedFirms.filter((id) => id !== firmId));
    }
  };

  const handleBulkAction = (action) => {
    toast.success(`${action} applied to ${selectedFirms.length} firms`);
    setSelectedFirms([]);
  };

  const handleDelete = async () => {
    if (!deleteFirm) return;

    try {
      await deletePropFirm(deleteFirm.id);
      toast.success('Firm deleted');
      setDeleteFirm(null);
      fetchFirms();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete firm');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedFirms.map((id) => deletePropFirm(id)));
      toast.success(`${selectedFirms.length} firms deleted`);
      setSelectedFirms([]);
      setBulkDeleteOpen(false);
      fetchFirms();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete firms');
    }
  };

  // Safe formatters for API data
  const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    return isNaN(num) ? value : `$${num.toLocaleString()}`;
  };

  const formatRating = (value) => {
    if (value === null || value === undefined) return '—';
    return Number(value).toFixed(1);
  };

  const LOGO_BASE_URL = "https://prop-api.8100081.xyz/prop-firms/firmlogo";

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("http://") || logoUrl.startsWith("https://")) {
    return logoUrl;
  }
  return `${LOGO_BASE_URL}/${logoUrl}`;
};

  return (
    <div>
      <PageHeader
        title="Prop Firms"
        description="Manage all prop firms in your directory"
        breadcrumbs={['Admin', 'Content', 'Prop Firms']}
        actions={
          <Button onClick={() => navigate('/dashboard/firms/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Firm
          </Button>
        }
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search firms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Actions Bar */}
          {selectedFirms.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedFirms.length} firm{selectedFirms.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('Activate')}
                >
                  Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('Draft')}
                >
                  Set as Draft
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="text-red-600 hover:text-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={
                            filteredFirms.length > 0 &&
                            selectedFirms.length === filteredFirms.length
                          }
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Firm
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Status
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Rating
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                        Starting Price
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                        Max Funding
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Reviews
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">
                        Featured
                      </TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFirms.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                          No firms found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredFirms.map((firm) => (
                        <TableRow key={firm.id} className="hover:bg-slate-50">
                          <TableCell>
                            <Checkbox
                              checked={selectedFirms.includes(firm.id)}
                              onCheckedChange={(checked) =>
                                handleSelectFirm(firm.id, checked)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {getLogoUrl(firm.logo_url) ? (
                                <img
                                  src={getLogoUrl(firm.logo_url)}
                                  alt={firm.name}
                                  className="h-10 w-10 rounded object-cover"
                                  onError={(e) => {
                                    e.currentTarget.src = "";
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-500">
                                  {firm.name?.charAt(0) || "?"}
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-slate-900">
                                  {firm.name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {firm.country || firm.slug || '—'}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={firm.status} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                              <span className="text-sm font-medium text-slate-900">
                                {formatRating(firm.avg_rating)}
                              </span>
                              <span className="text-xs text-slate-500">
                                ({firm.review_count ?? 0})
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-sm font-medium text-slate-900">
                              {formatPrice(firm.min_starting_price)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-sm font-medium text-slate-900">
                              {formatPrice(firm.max_funding)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3 text-blue-500" />
                              <span className="text-sm text-slate-600">
                                {firm.review_count ?? 0}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={!!firm.featured_order || !!firm.featured}
                              onCheckedChange={() =>
                                handleToggleFeatured(
                                  firm.id,
                                  !!firm.featured_order || !!firm.featured
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/dashboard/firms/${firm.id}`)
                                  }
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/dashboard/firms/${firm.id}/edit`)
                                  }
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => setDeleteFirm(firm)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-600">
                  Showing{' '}
                  <span className="font-medium">{filteredFirms.length}</span> of{' '}
                  <span className="font-medium">{firms.length}</span> firms
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled>
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Single Delete */}
      <ConfirmDialog
        open={!!deleteFirm}
        onOpenChange={() => setDeleteFirm(null)}
        title="Delete Firm"
        description={`Are you sure you want to delete "${deleteFirm?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />

      {/* Bulk Delete */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Firms"
        description={`Are you sure you want to delete ${selectedFirms.length} firm${
          selectedFirms.length > 1 ? 's' : ''
        }? This action cannot be undone.`}
        confirmLabel="Delete All"
        variant="destructive"
        onConfirm={handleBulkDelete}
      />
    </div>
  );
};